
import { type PropType, computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from "vue";

import { type EmitSearchEnter, type SearchItem, type CAutocomplete as TypeCAutocomplete } from "./types";
import MissingAvatar from "../../assets/missing-avatar.png";
import { CAutocomplete, CIcon, CListItemAvatar, CListItemIcon, CListItemTitle } from "../../vuetify-components";
import { CImage } from "../CImage";

export default defineComponent({
  components: { CAutocomplete, CImage, CIcon, CListItemAvatar, CListItemTitle, CListItemIcon },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<SearchItem[]>,
      required: true,
    },
    searchTerm: {
      type: String,
      default: "",
    },
    isShaped: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  emits: {
    "search-enter": (_v: EmitSearchEnter) => true,
    "search-select": (_v: EmitSearchEnter) => true,
    "search-click": (_v: string) => true,
    "search-update": (_v: string) => true,
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy;
    const autocomplete = ref<TypeCAutocomplete | undefined>();
    const select = ref<SearchItem>({
      objectID: props.searchTerm,
      query: props.searchTerm,
    });

    const search = ref(props.searchTerm);
    const isMenuActive = ref(false);
    const menuWidth = ref("0");

    onMounted(() => {
      // only shape autocomplete when the dropdown list opens
      // calling vm.$watch because traditional watcher doesn't work for some reasons
      // TODO: see if composetion api fixes this
      vm?.$watch("autocomplete.isMenuActive", (newValue: boolean) => {
        isMenuActive.value = newValue;
      });

      // set autocomplete dropdown list max width
      onResize();
    });

    const onEnter = () => {
      select.value = { objectID: search.value, query: search.value };
      emit("search-enter", { search: search.value, select: select.value });

      if (autocomplete.value !== undefined) {
        autocomplete.value.isMenuActive = false;
        autocomplete.value.blur();
      }
    };

    const onSelect = () => {
      search.value = select.value?.query;
      emit("search-select", { search: search.value, select: select.value });
      autocomplete.value?.blur();
    };

    const onUpdate = () => {
      // only emit event if typing
      if (autocomplete.value?.isFocused) {
        emit("search-update", search.value);
      }
    };

    const onResize = () => {
      menuWidth.value = props.isShaped
        ? `${(autocomplete.value?.$el as HTMLElement | undefined)?.offsetWidth}`
        : "100vw";
    };

    const menuProps = computed(() => {
      // Fix height to always be small enough to not cover search bar
      const maxHeight = "calc(100vh - 65px)";

      return {
        offsetY: true,
        maxWidth: menuWidth.value,
        maxHeight,
        minWidth: vm?.$vuetify.breakpoint.mdAndUp ? undefined : menuWidth.value,
      };
    });

    const placeHolderText = computed(() => {
      const placeHolder = props.placeholder;
      if (placeHolder) {
        return placeHolder;
      }
      return vm?.$gettext("Search fashion, home, & more");
    });

    watch(
      () => props.searchTerm,
      (newVal: string) => {
        select.value = {
          objectID: newVal,
          query: newVal,
        };
        search.value = newVal;
      },
      { immediate: true },
    );

    return {
      select,
      search,
      isMenuActive,
      menuProps,
      placeHolderText,
      MissingAvatar,
      autocomplete,
      onEnter,
      onSelect,
      onResize,
      onUpdate,
    };
  },
});
